import { FunctionComponent, memo, useEffect, useMemo, useState } from "react";
import { FormProvider, SubmitHandler, useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { addExamModel } from "../../Redux/Slices/ExamModel/AddExamModelSlice";
import { EditExamModel } from "../../Redux/Slices/ExamModel/EditExamModelSlice";
import Select from "../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";
import TextInputFieldC from "../../Components/TextInput/TextInputFieldC";
import { IExamModel } from "./IExamModel";
import { Button, ButtonGroup, IconButton, Radio, RadioGroup, FormControlLabel, Box, Typography, Checkbox, Stack } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import NumberInputField from "../../Components/NumberInput/NumberInputField";
import {
  clearlistQuestionSubTypeResponse,
  listQuestionSubType,
} from "../../Redux/Slices/QuestionSubType/ListQuestionSubTypeSlice";
import { listQuestionMapping } from "../../Redux/Slices/Questions/QuestionMappingSlice";
import NumberInputFieldlist from "../../Components/NumberInputlist/NumberInputFieldlist";
import PredefinedMappingLayout from "../Questions/PredefinedMappingLayout";
import ResponseDialog from "../../Components/ResponseDialog/ResponseDialog";
import { IAPIResponse } from "../../Redux/interfaces/IAPIResponse";
import AlterDialog from "../../Components/AlterDialog/AlterDialog";
import { MessageDialogProps } from "../../Components/AlterDialog/IAlterDialog";
interface ExamModelFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: IExamModel;
  setInitialValues: React.Dispatch<React.SetStateAction<IExamModel>>;
  onFullScreenToggle?: (full: boolean) => void;
}
// Define the Zod schema with minimum value requirements
const userSchema = z.object({
  id: z.number().optional(),
  model_name:
    z.string().min(1, { message: "Please enter model name" })
      .max(50, { message: "model namecannot exceed 50 characters" }),
  questionTypes: z.number().nullable().optional(),
  question_counts: z.coerce.number().min(1, { message: "Please enter question count" }).optional().or(z.literal("")),
  durations: z.coerce.number().min(1, { message: "Please enter duration" }).optional().or(z.literal("")),
  sequence: z.coerce.number().min(1, { message: "Please enter sequence" }).optional().or(z.literal("")),
  subtypes: z.number().nullable().optional(),
  subtype_counts: z.coerce.number().min(1, { message: "Please enter subtype count" }).optional().or(z.literal("")),
  subtype_sequences: z.coerce.number().min(1, { message: "Please enter subtype sequence" }).optional().or(z.literal("")),
  question_selection_type: z.enum(['random', 'predefined']).optional(),
  question_mappings: z.any().optional(),
  question_sequences: z.any().optional(),
  status: z.boolean({ message: "Please select status" }),
  free_tier: z.boolean().optional(),
});
const ExamModelForm: FunctionComponent<ExamModelFormProps> = (props) => {
  const dispatch = useAppDispatch();
  // Fetching question types from Redux
  const { data: listQuestions } = useAppSelector(
    (state) => state.ListQuestionTypeSlice
  );
  const { data: questionMappingData } = useAppSelector(
    (state) => state.QuestionMappingSlice
  );

  const QuestionList: AutocompleteOption[] = useMemo(
    () =>
      Array.isArray(listQuestions?.data)
        ? listQuestions?.data
          ?.filter((role) => role.Status !== false)
          .map((listQuestions) => ({
            label: listQuestions?.name!,
            value: listQuestions.id,
          })) ?? []
        : [],
    [listQuestions?.data]
  );
  const { data: listQuestionssub } = useAppSelector(
    (state) => state.ListQuestionSubTypeSlice
  );
  const QuestionsubList: AutocompleteOption[] = Array.isArray(
    listQuestionssub?.data
  )
    ? listQuestionssub?.data
      ?.filter((role) => role.status !== false)
      .map((listQuestions) => ({
        label: listQuestions?.subtype_name!,
        value: listQuestions.id,
      })) ?? []
    : [];

  const methods = useForm<IExamModel>({
    defaultValues: {
      id: props.initialValues.id,
      model_name: props.initialValues.model_name || "",
      question_selection_type: (props.initialValues.id || props.initialValues.questionTypes)
        ? (props.initialValues.question_selection_type ||
          (props.initialValues.question_mappings?.some((m: any) => Array.isArray(m) ? m.length > 0 : m)
            ? "predefined"
            : "random"))
        : "random",
      status: props.initialValues.status ?? true,
    },
    resolver: zodResolver(userSchema),
    mode: "onChange",
  });
  // State to store list of added entries
  const [displayDataListsub, setDisplayDataListsub] = useState<
    {
      subtypes?: { label: string; value: number } | null;
      question_counts?: number | null;
      questionTypes?: { label: string; value: number } | null;
      subtype_counts?: number | null;
      subtype_sequences?: number | null;
      durations?: number | null;
      sequence?: number | null;
      question_mappings?: number[];
      free_tier?: boolean;
    }[]
  >([]);
  const [alertResponse, setAlertResponse] = useState<IAPIResponse<any>>({} as IAPIResponse<any>);
  const [editingSub, setEditingSub] = useState<number | null>(null);
  const [showSwitchWarning, setShowSwitchWarning] = useState(false);
  const [pendingSwitchData, setPendingSwitchData] = useState<{ type: 'type' | 'subtype', value: any } | null>(null);
  // Add new data to the list
  const handleAddDatasub = () => {
    const questionTypes = methods.getValues("questionTypes");
    const subtypes = methods.getValues("subtypes");
    const subtype_counts = methods.getValues("subtype_counts");
    const subtype_sequences = methods.getValues("subtype_sequences");
    const durations = methods.getValues("durations");
    const sequence = methods.getValues("sequence");
    if (!questionTypes) {
      methods.setError("questionTypes", { type: "manual", message: "Please select a question type first." });
      return;
    }
    const selectedQuestionTypesub = QuestionsubList?.find(
      (list) => list.value === subtypes
    );
    const selectedQuestionType = QuestionList?.find(
      (list) => list.value === questionTypes
    );
    if (!selectedQuestionType) {
      methods.setError("questionTypes", { type: "manual", message: "Selected question type not found." });
      return;
    }
    if (!selectedQuestionTypesub) {
      methods.setError("subtypes", { type: "manual", message: "Selected subtype not found." });
      return;
    }
    const newItem = {
      questionTypes: selectedQuestionType,
      subtypes: selectedQuestionTypesub,
      subtype_counts,
      subtype_sequences,
      durations,
      sequence,
      question_mappings: selectedQuestions || [],
    };
    if (editingSub !== null) {
      // Update existing
      setDisplayDataListsub((prevList: any) =>
        prevList.map((item: any, i: any) => (i === editingSub ? newItem : item))
      );
      setEditingSub(null);
    } else {
      // Check for duplicate
      const isDuplicatesub = displayDataListsub.some(
        (item, index) =>
          item.questionTypes?.value === questionTypes &&
          item.subtypes?.value === subtypes &&
          index !== editingSub
      );
      if (isDuplicatesub) {
        methods.setError("subtypes", { type: "manual", message: "Question subtype combination already exists." });
        return;
      }
      // Add new
      setDisplayDataListsub((prevList: any) => [...prevList, newItem]);
    }
    // reset
    // methods.resetField("questionTypes");
    methods.resetField("subtype_counts", { defaultValue: "" });
    methods.resetField("subtypes");
    methods.resetField("subtype_sequences", { defaultValue: "" });
    return true; // Return success
  };
  const handleEditSub = (index: number) => {
    const item = displayDataListsub[index];
    methods.setValue("questionTypes", item.questionTypes?.value);
    methods.setValue("subtypes", item.subtypes?.value);
    methods.setValue("subtype_counts", item.subtype_counts);
    methods.setValue("subtype_sequences", item.subtype_sequences);
    methods.setValue("question_counts", item.question_counts);
    methods.setValue("durations", item.durations);
    methods.setValue("sequence", item.sequence);
    methods.setValue("free_tier", item.free_tier || false);

    setSelectedQuestions(item.question_mappings || []);
    setEditingSub(index);
    setEditingMain(null); // Ensure we are not editing a main
  };
  // Delete data by index
  const handleDeleteDatasub = (index: number) => {
    setDisplayDataListsub((prevList) => prevList.filter((_, i) => i !== index));
  };
  // State to store list of added entries
  const [displayDataList, setDisplayDataList] = useState<
    {
      questionTypes?: { label: string; value: number } | null;
      question_counts?: number | null;
      durations?: number | null;
      sequence?: number | null;
      question_mappings?: number[];
      free_tier?: boolean;
    }[]
  >([]);
  const [editingMain, setEditingMain] = useState<number | null>(null);
  // console.warn("displayList", displayDataList)

  // Selected questions for the current question type being added/edited
  const [selectedQuestions, setSelectedQuestions] = useState<number[]>([]);

  const allStagedIds = useMemo(() => {
    const mainIds = displayDataList
      .filter((_, index) => index !== editingMain)
      .flatMap(item => item.question_mappings || []);
    const subIds = displayDataListsub
      .filter((_, index) => index !== editingSub)
      .flatMap(item => (item as any).question_mappings || []);
    return Array.from(new Set([...mainIds, ...subIds]));
  }, [displayDataList, displayDataListsub, editingMain, editingSub]);

  const selectionType = methods.watch("question_selection_type");
  useEffect(() => {
    props.onFullScreenToggle?.(selectionType === 'predefined');
  }, [selectionType, props.onFullScreenToggle]);

  const handleSwitchConfirm = (action: 'add' | 'discard') => {
    if (!pendingSwitchData) return;

    if (action === 'add') {
      const isSubtype = methods.watch("questionTypes") && QuestionsubList.length > 0;
      if (isSubtype) {
        handleAddDatasub();
      } else {
        handleAddData(selectedQuestions);
      }
    }

    // Perform the switch
    if (pendingSwitchData.type === 'type') {
      methods.setValue("questionTypes", pendingSwitchData.value ?? null, {
        shouldValidate: true,
      });
      if (pendingSwitchData.value) {
        dispatch(listQuestionSubType({ questiontype_id: pendingSwitchData.value }));
      }
      methods.setValue("subtypes", null); // Clear subtype when type changes
    } else {
      methods.setValue("subtypes", pendingSwitchData.value ?? null, {
        shouldValidate: true,
      });
    }

    if (action === 'discard') {
      setSelectedQuestions([]);
    }

    setShowSwitchWarning(false);
    setPendingSwitchData(null);
  };


  // Add new data to the list
  const handleAddData = (questionMappings?: number[]) => {
    const questionTypes = methods.getValues("questionTypes");
    const question_counts = methods.getValues("question_counts");
    const durations = methods.getValues("durations");
    const sequence = methods.getValues("sequence");
    const free_tier = methods.getValues("free_tier");
    const selectedQuestionType = QuestionList?.find(
      (list) => list.value === questionTypes
    );
    if (!selectedQuestionType) {
      setAlertResponse({
        status: false,
        message: "Please select a Question Type.",
        data: null,
        status_code: 400
      });
      return false;
    }

    // Use the questionMappings from parameter or fall back to selectedQuestions state
    const finalQuestionMappings = questionMappings || selectedQuestions || [];

    const newData: any = {
      questionTypes: selectedQuestionType,
      question_counts: question_counts,
      durations: durations,
      sequence: sequence,
      free_tier: !!free_tier,
      question_mappings: finalQuestionMappings,
    };
    if (editingMain !== null) {
      // Update existing
      setDisplayDataList((prevList: any) =>
        prevList.map((item: any, i: any) =>
          i === editingMain ? { ...newData, question_mappings: finalQuestionMappings || item.question_mappings || [] } : item
        )
      );
      setEditingMain(null);
    } else {
      // Check if the selected question type already exists in the display data list
      const isDuplicate = displayDataList.some(
        (item, index) => item.questionTypes?.value === questionTypes && index !== editingMain
      );
      // If not duplicate and both values are present, add the new data
      if (isDuplicate) {
        setAlertResponse({
          status: false,
          message: "Question type already exists.",
          data: null,
          status_code: 400
        });
        return false;
      }
      if (questionTypes && question_counts && durations && sequence) {
        setDisplayDataList((prevList: any) => [...prevList, newData]);
      } else {
        setAlertResponse({
          status: false,
          message: "Please fill all required fields (Question Count, Duration, Sequence).",
          data: null,
          status_code: 400
        });
        return false;
      }
    }
    // reset
    methods.resetField("question_counts", { defaultValue: "" });
    // methods.resetField("questionTypes");
    methods.resetField("durations", { defaultValue: "" });
    methods.resetField("sequence", { defaultValue: "" });
    methods.resetField("free_tier", { defaultValue: false });
    return true; // Return success
  };
  const handleEditData = (index: number) => {
    const item = displayDataList[index];
    methods.setValue("questionTypes", item.questionTypes?.value);
    methods.setValue("question_counts", item.question_counts);
    methods.setValue("durations", item.durations);
    methods.setValue("sequence", item.sequence);
    methods.setValue("free_tier", item.free_tier || false);

    // clear subtype fields
    methods.setValue("subtypes", null);
    methods.setValue("subtype_counts", "");
    methods.setValue("subtype_sequences", "");

    setSelectedQuestions(item.question_mappings || []);
    setEditingMain(index);
    setEditingSub(null); // Ensure we are not editing a sub
  };
  // Delete data by index
  const handleDeleteData = (index: number) => {
    const itemToDelete = displayDataList[index];
    const questionTypeValue = itemToDelete?.questionTypes?.value;

    // Remove from displayDataList
    setDisplayDataList((prevList) => prevList.filter((_, i) => i !== index));

    // Remove from displayDataListsub
    if (questionTypeValue) {
      setDisplayDataListsub((prevList) =>
        prevList.filter(
          (item: any) => item.questionTypes?.value !== questionTypeValue
        )
      );
    }
  };
  // Submit handler
  const QuestionTypes: any = displayDataList.map(
    (item: any) => item.questionTypes?.value
  );
  // Step 2: Create a map from groupedData
  const groupedDataMap = displayDataListsub.reduce((acc: any, item: any) => {
    const key = item.questionTypes?.value;
    if (key && !acc[key]) {
      acc[key] = { subtypes: [], subtype_counts: [], subtype_sequences: [] };
    }
    if (key) {
      acc[key].subtypes.push(item.subtypes?.value);
      acc[key].subtype_counts.push(item.subtype_counts);
      acc[key].subtype_sequences.push(item.subtype_sequences);
    }
    return acc;
  }, {});
  // Run validation
  const validationMessages = validateQuestionCounts(
    displayDataList,
    displayDataListsub
  );
  let missingTypes: any = [];
  function checkMissingTypes(mainList: any, subList: any) {
    const mainTypeValues = new Set(
      mainList.map((item: any) => item.questionTypes?.value).filter(Boolean)
    );
    missingTypes = []; // clear/reset it
    subList.forEach((sub: any) => {
      const type = sub.questionTypes;
      if (type?.value && !mainTypeValues.has(type.value)) {
        missingTypes.push(type.label);
      }
    });
  }
  // Call the function
  checkMissingTypes(displayDataList, displayDataListsub);
  // Use it outside
  if (missingTypes.length > 0) {
    console.log(`Please add the question types: ${missingTypes.join(", ")}`);
  }
  const onSubmit: SubmitHandler<IExamModel> = (val) => {
    // Validate model_name for predefined mode
    if (val.question_selection_type === 'predefined' && !val.model_name?.trim()) {
      setAlertResponse({
        status: false,
        message: 'Model Name is required for predefined questions mode.',
        data: null,
        status_code: 400
      });
      return;
    }

    const isPredefined = val.question_selection_type === 'predefined';
    const limit = (methods.watch("subtype_counts") as number) || (methods.watch("question_counts") as number) || 0;

    const hasValidData = isPredefined ?
      (selectedQuestions.length > 0 || displayDataList.length > 0 || displayDataListsub.length > 0) :
      (displayDataList.length >= 1 || displayDataListsub.length >= 1);

    // Debug: Log selected questions for predefined mode
    if (isPredefined) {
      // console.log('Selected questions:', selectedQuestions);
      // console.log('Display data list:', displayDataList);
    }

    if (validationMessages.some((msg: any) => msg.trim() === "") && hasValidData) {
      let finalDisplayList = displayDataList;

      // Ensure finalDisplayList includes all question types, even if only in subtypes list
      const allQuestionTypes = Array.from(new Set([
        ...displayDataList.map(item => item.questionTypes?.value),
        ...displayDataListsub.map(item => item.questionTypes?.value)
      ])).filter(Boolean);

      finalDisplayList = allQuestionTypes.map(qtValue => {
        const mainItem = displayDataList.find(item => item.questionTypes?.value === qtValue);
        const subItems = displayDataListsub.filter(item => item.questionTypes?.value === qtValue);
        const selectedQuestionType = QuestionList?.find(q => q.value === qtValue);

        return {
          questionTypes: {
            label: selectedQuestionType?.label || "Unknown",
            value: (selectedQuestionType?.value || qtValue) as number
          },
          question_counts: Number(mainItem?.question_counts || subItems.reduce((sum, sub) => sum + Number(sub.subtype_counts || 0), 0) || 0),
          durations: Number(mainItem?.durations || (subItems.length > 0 ? subItems[0].durations : 30)),
          sequence: Number(mainItem?.sequence || (subItems.length > 0 ? subItems[0].sequence : 1)),
          question_mappings: [
            ...(mainItem?.question_mappings || []),
            ...subItems.flatMap(sub => sub.question_mappings || [])
          ]
        };
      });

      // Special case for predefined mode with zero items in lists but active selections
      if (isPredefined && finalDisplayList.length === 0) {
        const selectedQuestionTypeId = val.questionTypes as unknown as number;
        const selectedQuestionType = QuestionList?.find(q => q.value === selectedQuestionTypeId);
        if (selectedQuestionTypeId) {
          finalDisplayList = [{
            questionTypes: {
              label: selectedQuestionType?.label || "Unknown",
              value: (selectedQuestionType?.value || selectedQuestionTypeId) as number
            },
            question_counts: selectedQuestions.length,
            durations: val.durations || 30,
            sequence: val.sequence || 1,
            question_mappings: selectedQuestions
          }];
        }
      }

      // Format data according to API specification
      const apiPayload = {
        model_name: val.model_name,
        questionTypes: finalDisplayList.map((item) => item.questionTypes?.value || 0),
        question_counts: finalDisplayList.map((item) => item.question_counts || 0),
        durations: finalDisplayList.map((item) => item.durations || 0),
        sequence: finalDisplayList.map((item) => item.sequence || 0),
        subtypes: finalDisplayList.map((item) => {
          const qt = item.questionTypes?.value;
          const subs = groupedDataMap[qt!]?.subtypes;
          return subs && subs.length > 0 ? subs : [];
        }),
        subtype_counts: finalDisplayList.map((item) => {
          const qt = item.questionTypes?.value;
          const counts = groupedDataMap[qt!]?.subtype_counts;
          return counts && counts.length > 0 ? counts.map(Number) : [0];
        }),
        subtype_sequences: finalDisplayList.map((item) => {
          const qt = item.questionTypes?.value;
          const seqs = groupedDataMap[qt!]?.subtype_sequences;
          return seqs && seqs.length > 0 ? seqs.map(Number) : [];
        }),
        question_mappings: finalDisplayList.map((item) => {
          const mappings = item.question_mappings || [];
          return Array.from(new Set(mappings));
        }),
        question_sequences: finalDisplayList.map((item) => {
          const mappings = item.question_mappings || [];
          // Generate sequence numbers based on the order of selected questions
          return mappings.map((_, index) => index + 1);
        }),
        instructions: finalDisplayList.map((item) => [""]), // Empty instructions for now, can be configured later
        status: val.status ?? true,
      };

      console.log('Final API Payload:', apiPayload);

      if (val.id) {
        // Edit mode
        dispatch(EditExamModel({ ...apiPayload, id: val.id }));
      } else {
        // Add mode
        dispatch(addExamModel(apiPayload));
      }
    } else if (!hasValidData) {
      alert(isPredefined ?
        'Please select at least one question for the predefined mode.' :
        'Please add at least one question type or subtype to the exam model.'
      );
    }
  };
  const questionTypes = methods.getValues("questionTypes");
  const isDuplicate = displayDataList.some(
    (item) => item.questionTypes?.value === questionTypes
  );
  const questionTypessub = methods.getValues("subtypes");
  const isDuplicatesub = displayDataListsub.some(
    (item) =>
      item.subtypes?.value === questionTypessub &&
      item.questionTypes?.value === methods.getValues("questionTypes")
  );
  useEffect(() => {
    return () => {
      props.setInitialValues({});
    };
  }, []);
  useEffect(() => {
    // Perform the effect for main list
    if (
      (props.initialValues.id || props.initialValues.questionTypes) &&
      QuestionList?.length > 0 &&
      props.initialValues.questionTypes
    ) {
      const questionTypes: any = props.initialValues.questionTypes; // [10, 9, 13]
      const question_counts: any = props.initialValues.question_counts; // [11, 111, 2]
      const durations: any = props.initialValues.durations; // [11, 111, 2]
      const sequence: any = props.initialValues.sequence;
      const question_mappings: any = props.initialValues.question_mappings;
      const question_sequences: any = props.initialValues.question_sequences;

      // Map both arrays into the required format
      const mappedData = questionTypes?.map((typeValue: any, index: any) => {
        const selectedQuestionType = QuestionList?.find(
          (list) => list.value === typeValue
        );

        // Get mappings and sequences for this question type
        const mappings = question_mappings ? (question_mappings[index] || []) : [];
        const sequences = question_sequences ? (question_sequences[index] || []) : [];

        // Pair and sort by sequence if lengths match
        let sortedMappings = [...mappings];
        if (sequences.length === mappings.length && sequences.length > 0) {
          const pairs = mappings.map((id: any, i: any) => ({ id, seq: Number(sequences[i]) }));
          pairs.sort((a: any, b: any) => a.seq - b.seq);
          sortedMappings = pairs.map((p: any) => p.id);
        }

        return {
          questionTypes: selectedQuestionType || {
            label: "Unknown",
            value: typeValue,
          },
          question_counts: question_counts[index] || null,
          durations: durations[index] || null,
          sequence: sequence[index] || null,
          question_mappings: sortedMappings,
          question_sequences: sequences,
        };
      });
      setDisplayDataList(mappedData || []);
      // You can now use the mappedData for further logic if needed
    }
  }, [props.initialValues, QuestionList]);
  useEffect(() => {
    if (
      (props.initialValues.id || props.initialValues.subtypes) &&
      QuestionsubList?.length > 0 &&
      props.initialValues.subtypes
    ) {
      const subtypes: any = props.initialValues.subtypes; // [[5, 7], [6]]
      const questionTypes: any = props.initialValues.questionTypes; // [1, 2]
      const subtype_counts: any = props.initialValues.subtype_counts; // [[23, 21], [34]]
      const subtype_sequences: any = props.initialValues.subtype_sequences; // [[1, 12], [566666]]
      const question_counts: any = props.initialValues.question_counts;
      const durations: any = props.initialValues.durations;
      const sequence: any = props.initialValues.sequence;
      const question_mappings: any = props.initialValues.question_mappings;
      const question_sequences: any = props.initialValues.question_sequences;

      const mappedData = questionTypes?.map((typeValue: any, index: any) => {
        const selectedQuestionType = QuestionList?.find(
          (list) => list.value === typeValue
        );
        const mappedSubtypes = subtypes[index]?.map(
          (subtypeValue: any, subIndex: any) => {
            const selectedSubtype = QuestionsubList?.find(
              (sub) => sub?.value === subtypeValue
            );
            return {
              subtypes: selectedSubtype
                ? {
                  label: selectedSubtype.label,
                  value: selectedSubtype.value,
                }
                : { label: "Unknown", value: subtypeValue },
              subtype_counts: subtype_counts[index]?.[subIndex] || null,
              subtype_sequences: subtype_sequences[index]?.[subIndex] || null,
            };
          }
        );
        // Get mappings and sequences for this question type
        const mappings = question_mappings ? (question_mappings[index] || []) : [];
        const sequences = question_sequences ? (question_sequences[index] || []) : [];

        // Pair and sort by sequence if lengths match
        let sortedMappings = [...mappings];
        if (sequences.length === mappings.length && sequences.length > 0) {
          const pairs = mappings.map((id: any, i: any) => ({ id, seq: Number(sequences[i]) }));
          pairs.sort((a: any, b: any) => a.seq - b.seq);
          sortedMappings = pairs.map((p: any) => p.id);
        }

        return mappedSubtypes.map((subItem: any) => ({
          questionTypes: selectedQuestionType
            ? {
              label: selectedQuestionType.label,
              value: selectedQuestionType.value,
            }
            : { label: "Unknown", value: typeValue },
          question_counts: question_counts[index] || null,
          durations: durations[index] || null,
          sequence: sequence[index] || null,
          question_mappings: sortedMappings,
          question_sequences: sequences,
          ...subItem,
        }));
      });
      setDisplayDataListsub(mappedData.flat() || []);
    }
  }, [props.initialValues, QuestionsubList]);
  // Load subtypes for edit mode
  useEffect(() => {
    if ((props.initialValues.id || props.initialValues.questionTypes) && props.initialValues.questionTypes) {
      const uniqueQTs = Array.from(
        new Set(props.initialValues.questionTypes as number[])
      );
      uniqueQTs.forEach((qt) => {
        dispatch(listQuestionSubType({ questiontype_id: qt }));
      });
    }
  }, [props.initialValues.id, props.initialValues.questionTypes]);
  // Status list
  const RoleSatus = [
    {
      label: "Active",
      value: true,
    },
    {
      label: "Inactive",
      value: false,
    },
  ];
  function validateQuestionCounts(
    displayDataList: any,
    displayDataListsub: any
  ) {
    let messages: any = [];
    // Get unique question type values from displayDataListsub
    const subQuestionTypes = new Set(
      displayDataListsub
        .map((subItem: any) => subItem.questionTypes?.value)
        .filter(Boolean)
    );
    displayDataList.forEach((mainItem: any) => {
      const mainValue = mainItem.questionTypes?.value;
      const mainCount = mainItem.question_counts;
      // Skip if no mainValue
      if (!mainValue) return;
      // Only validate if the question type exists in displayDataListsub
      if (!subQuestionTypes.has(mainValue)) {
        return;
      }
      // Find all matching sub-items
      const matchingSubItems = displayDataListsub.filter(
        (subItem: any) => subItem.questionTypes?.value === mainValue
      );
      // Sum all subtype counts for this question type
      const totalSubCount = matchingSubItems.reduce(
        (sum: any, subItem: any) => sum + subItem.subtype_counts,
        0
      );
      if (totalSubCount < mainCount) {
        messages.push(
          `Total question count across ${mainItem.questionTypes?.label
          } subtypes must match the required count for the question type
          Please add ${mainCount - totalSubCount} more.`
        );
      } else if (totalSubCount > mainCount) {
        messages.push(
          `Total question count across ${mainItem.questionTypes?.label
          } subtypes must match the required count for the question type
          Please remove ${totalSubCount - mainCount} to align.`
        );
      }
    });
    return messages.length > 0 ? messages : [""];
  }
  const FIlterdata: any = QuestionList?.filter((data) => {
    return data?.value === methods.getValues("questionTypes");
  });
  const isMainFieldsFilled =
    methods.watch("questionTypes") &&
    methods.watch("question_counts") &&
    methods.watch("durations") &&
    methods.watch("sequence");
  const isSubFieldsFilled =
    methods.watch("questionTypes") && // Added this check to disable if no question type selected
    methods.watch("subtypes") &&
    methods.watch("subtype_counts") &&
    methods.watch("subtype_sequences");
  const {
    formState: { errors },
  } = methods;
  return (
    <>
      <FormProvider {...methods}>
        <form
          ref={props.formRef}
          onSubmit={methods.handleSubmit(onSubmit)}
          // className="form_double_input"
          style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "20px", marginTop: "10px" }}
        >
          {methods.watch("question_selection_type") !== 'predefined' && (
            <>
              {/* Input for Model Name */}
              <div style={{ display: "flex", flexDirection: "column" }}>
                <TextInputFieldC name="model_name" label="Model Name" type="text" />
              </div>

              {/* Question Selection Type */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Question Selection Type
                </Typography>
                <RadioGroup
                  row
                  value={methods.getValues("question_selection_type")}
                  onChange={(e) => {
                    methods.setValue("question_selection_type", e.target.value as 'random' | 'predefined', { shouldValidate: true });
                  }}
                >
                  <FormControlLabel
                    value="random"
                    control={<Radio />}
                    label="Random Questions"
                  />
                  <FormControlLabel
                    value="predefined"
                    control={<Radio />}
                    label="Predefined Questions"
                  />
                </RadioGroup>
              </Box>
            </>
          )}

          {methods.watch("question_selection_type") === 'predefined' ? (
            <Box sx={{ mb: -4 }}> {/* Adjust margin as PredefinedMappingLayout has internal padding */}
              {/* Model Name field for predefined mode */}


              <PredefinedMappingLayout
                selectedIds={selectedQuestions}
                onSelectionChange={setSelectedQuestions}
                onAdd={() => {
                  const isSubtype = methods.getValues("subtypes");
                  let added = false;
                  if (isSubtype) {
                    added = handleAddDatasub() as unknown as boolean;
                  } else {
                    added = handleAddData(selectedQuestions) as unknown as boolean;
                  }

                  if (added) {
                    setSelectedQuestions([]);
                  }
                }}
                displayDataList={displayDataList}
                displayDataListsub={displayDataListsub}
                onDeleteMain={handleDeleteData}
                onDeleteSub={handleDeleteDatasub}
                onEditMain={handleEditData}
                onEditSub={handleEditSub}
                excludeId={methods.getValues("id") ? methods.getValues("id") : props.initialValues.id}
                excludeType="exam"
                stagedIds={allStagedIds}
                statusOptions={RoleSatus}
                isEditing={editingMain !== null || editingSub !== null}
              />
            </Box>
          ) : (
            <div className="twofieldlist">
              {/* Select for Question Type */}
              <div
                style={{ display: "flex", flexDirection: "column", width: "50%" }}
              >
                <Select
                  name="questionTypes"
                  label="Question Type"
                  sx={{ width: "100%" }}
                  options={QuestionList ?? []}
                  value={
                    QuestionList?.find(
                      (list) => list.value === methods.getValues("questionTypes")
                    ) ?? null
                  }
                  onChange={(_, value) => {
                    const val = value as AutocompleteOption;
                    if (selectedQuestions.length > 0 && val?.value !== methods.getValues("questionTypes")) {
                      setPendingSwitchData({ type: 'type', value: val?.value });
                      setShowSwitchWarning(true);
                      return;
                    }
                    methods.setValue("questionTypes", val?.value ?? null, {
                      shouldValidate: true,
                    });

                    // Dynamic Syncing: Check if this type already exists in cards
                    const existingIndex = displayDataList.findIndex(item => item.questionTypes?.value === val?.value);
                    if (existingIndex !== -1) {
                      const item = displayDataList[existingIndex];
                      methods.setValue("question_counts", item.question_counts);
                      methods.setValue("durations", item.durations);
                      methods.setValue("sequence", item.sequence);
                      methods.setValue("free_tier", item.free_tier || false);
                      setSelectedQuestions(item.question_mappings || []);
                      setEditingMain(existingIndex);
                      setEditingSub(null);
                    } else {
                      setEditingMain(null);
                      methods.resetField("question_counts", { defaultValue: "" });
                      methods.resetField("durations", { defaultValue: "" });
                      methods.resetField("sequence", { defaultValue: "" });
                      methods.resetField("free_tier", { defaultValue: false });
                      setSelectedQuestions([]);
                    }

                    if (val?.value) {
                      dispatch(listQuestionSubType({ questiontype_id: val.value }));
                    }
                    methods.setValue("subtypes", null);
                    methods.resetField("subtype_counts", { defaultValue: "" });
                    methods.resetField("subtype_sequences", { defaultValue: "" });
                  }}
                />
                {errors.questionTypes && (
                  <span
                    style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}
                  >
                    {errors.questionTypes.message?.toString() || "Please select a question type"}
                  </span>
                )}
              </div>
              {/* {selected === "without Subtype" && ( */}
              <div
                style={{ display: "flex", flexDirection: "column", width: "25%" }}
              >
                <NumberInputFieldlist
                  type="number"
                  sx={{ width: "100%" }}
                  name="question_counts"
                  label="Question Count"
                  inputProps={{ max: FIlterdata[0]?.label === "Writing" ? 1 : 100 }}
                />
                {errors.question_counts && (
                  <span
                    style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}
                  >
                    {errors.question_counts.message?.toString()}
                  </span>
                )}
              </div>
              {/* )} */}
              <div
                style={{ display: "flex", flexDirection: "column", width: "20%" }}
              >
                <NumberInputField
                  type="number"
                  sx={{ width: "100%" }}
                  name="durations"
                  label="Duration in Minutes"
                />
                {errors.durations && (
                  <span
                    style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}
                  >
                    {errors.durations.message?.toString()}
                  </span>
                )}
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", width: "20%" }}
              >
                <NumberInputField
                  type="number"
                  sx={{ width: "100%" }}
                  name="sequence"
                  label="Sequence"
                />
                {errors.sequence && (
                  <span
                    style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}
                  >
                    {errors.sequence.message?.toString()}
                  </span>
                )}
              </div>
            </div>
          )}

          {methods.watch("question_selection_type") !== 'predefined' && (
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "20px" }}>
              {/* <FormControlLabel
                control={
                  <Controller
                    name="free_tier"
                    control={methods.control}
                    render={({ field }: { field: any }) => (
                      <Checkbox
                        {...field}
                        checked={!!field.value}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                }
                label={<Typography variant="body2">Free Tier</Typography>}
              /> */}
              <AddCircleOutlineIcon
                titleAccess={
                  editingMain !== null
                    ? "Update this entry"
                    : isDuplicate
                      ? "Question type already exists. Please add another Question type"
                      : !isMainFieldsFilled
                        ? "Please fill all fields before adding."
                        : ""
                }
                sx={{
                  fontSize: "40px",
                  color: "#009DFF",
                  cursor: "pointer",
                  opacity: editingMain !== null || isMainFieldsFilled ? 1 : 0.4,
                  pointerEvents:
                    editingMain !== null || isMainFieldsFilled ? "auto" : "none",
                }}
                onClick={() => {
                  const questionType = methods.getValues("questionTypes");
                  const questionCount = methods.getValues("question_counts");
                  const duration = methods.getValues("durations");
                  const seq = methods.getValues("sequence");
                  if (questionType && questionCount && duration && seq) {
                    handleAddData(selectedQuestions);
                    setSelectedQuestions([]); // Reset selection
                  } else {
                    if (!questionType) methods.setError("questionTypes", { message: "Required" });
                    if (!questionCount) methods.setError("question_counts", { message: "Required" });
                    if (!duration) methods.setError("durations", { message: "Required" });
                    if (!seq) methods.setError("sequence", { message: "Required" });
                  }
                }}
              />
            </div>
          )}
          {methods.watch("question_selection_type") !== 'predefined' && (
            <>
              {missingTypes.length > 0 && (
                <div className="Altermeassge">
                  <ReportGmailerrorredIcon
                    fontSize="small"
                    sx={{
                      marginTop: "-1px",
                      marginRight: "5px",
                      color: "rgb(0 129 255)",
                    }}
                  />
                  <div style={{ color: "rgb(0 129 255)" }}>
                    Please add the question types: {missingTypes.join(", ")}
                  </div>
                </div>
              )}
              {displayDataList.length >= 1 ? (
                ""
              ) : (
                <div className="Altermeassge">
                  <ReportGmailerrorredIcon
                    fontSize="small"
                    sx={{
                      marginTop: "-1px",
                      marginRight: "5px",
                      color: "rgb(0 129 255)",
                    }}
                  />
                  <div style={{ color: "rgb(0 129 255)" }}>
                    Please add question types and their corresponding question counts
                    with duration
                  </div>
                </div>
              )}
              {methods.watch("question_selection_type") !== 'predefined' && displayDataList.length > 0 && (
                <div
                  style={{
                    marginTop: "20px",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(45%, 1fr))", // 2 columns
                    gap: "10px",
                  }}
                >
                  {" "}
                  {displayDataList.map((data, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "10px",
                        backgroundColor: "lightgrey",
                        borderRadius: "5px",
                        padding: "5px",
                        // width: "50%",
                        boxShadow: "0px 3px 9px darkgray",
                      }}
                    >
                      <div>
                        <b>{data.questionTypes?.label ?? "Unknown"}</b>
                        {/* {selected === "without Subtype" && ( */}
                        <div>
                          <b>Question Count :</b> {data.question_counts ?? "N/A"}
                        </div>
                        {/* )} */}
                        <div>
                          <b>Durations :</b> {data.durations ?? "N/A"} Minutes
                        </div>
                        <div>
                          <b>Sequence :</b> {data.sequence ?? "N/A"}{" "}
                        </div>
                        <div>
                          <b>Free Tier :</b> {data.free_tier ? "Yes" : "No"}{" "}
                        </div>
                      </div>
                      {/* Buttons for each entry */}
                      <div style={{ display: "flex", gap: "5px" }}>
                        <IconButton
                          onClick={() => handleEditData(index)}
                          size="small"
                        >
                          <EditIcon color="primary" fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteData(index)}
                          size="small"
                        >
                          <DeleteIcon color="error" fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="twofieldlist">
                {/* Select for Question Type */}
                {/* Select for Question Subtype */}
                <div
                  style={{ display: "flex", flexDirection: "column", width: "50%" }}
                >
                  <Select
                    name="subtypes"
                    label="Question Subtype"
                    sx={{ width: "100%" }}
                    options={QuestionsubList ?? []}
                    disabled={
                      (displayDataList?.length ?? 0) < 1 || // no question type added yet
                      !methods.getValues("questionTypes") // no selected question type
                    }
                    value={
                      QuestionsubList?.find(
                        (list) => list.value === methods.getValues("subtypes")
                      ) ?? null
                    }
                    onChange={(_, value) => {
                      const val = value as AutocompleteOption;
                      if (selectedQuestions.length > 0 && val?.value !== methods.getValues("subtypes")) {
                        setPendingSwitchData({ type: 'subtype', value: val?.value });
                        setShowSwitchWarning(true);
                        return;
                      }
                      methods.setValue("subtypes", val?.value ?? null, {
                        shouldValidate: true,
                      });

                      // Dynamic Syncing for Subtypes
                      const currentType = methods.getValues("questionTypes");
                      const existingSubIndex = displayDataListsub.findIndex(
                        item => item.questionTypes?.value === currentType && item.subtypes?.value === val?.value
                      );

                      if (existingSubIndex !== -1) {
                        const item = displayDataListsub[existingSubIndex];
                        methods.setValue("subtype_counts", item.subtype_counts);
                        methods.setValue("subtype_sequences", item.subtype_sequences);
                        setSelectedQuestions(item.question_mappings || []);
                        setEditingSub(existingSubIndex);
                        setEditingMain(null);
                      } else {
                        setEditingSub(null);
                        methods.resetField("subtype_counts", { defaultValue: "" });
                        methods.resetField("subtype_sequences", { defaultValue: "" });
                        // Don't necessarily clear selectedQuestions if they were selecting for the main type?
                        // Actually, if they switch subtype, they probably want to start fresh for that subtype.
                        setSelectedQuestions([]);
                      }
                    }}
                  />
                  {errors.subtypes && (
                    <span
                      style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}
                    >
                      {errors.subtypes.message?.toString() || "Please select a subtype"}
                    </span>
                  )}
                </div>
                {/* Input for Question Counts */}
                <div
                  style={{ display: "flex", flexDirection: "column", width: "25%" }}
                >
                  <NumberInputField
                    type="number"
                    // disabled={(QuestionsubList?.length ?? 0) < 1}
                    disabled={(displayDataList?.length ?? 0) < 1}
                    sx={{ width: "100%" }}
                    name="subtype_counts"
                    label="Question Subcount"
                  />
                  {errors.subtype_counts && (
                    <span
                      style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}
                    >
                      {errors.subtype_counts.message?.toString()}
                    </span>
                  )}
                </div>
                <div
                  style={{ display: "flex", flexDirection: "column", width: "20%" }}
                >
                  <NumberInputField
                    type="number"
                    // disabled={(QuestionsubList?.length ?? 0) < 1}
                    disabled={(displayDataList?.length ?? 0) < 1}
                    sx={{ width: "100%" }}
                    name="subtype_sequences"
                    label="Sequence"
                  />
                  {errors.subtype_sequences && (
                    <span
                      style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}
                    >
                      {errors.subtype_sequences.message?.toString()}
                    </span>
                  )}
                </div>
                {(displayDataList?.length ?? 0) < 1 ? (
                  <AddCircleOutlineIcon sx={{ fontSize: "30px", color: "gray" }} />
                ) : (
                  <AddCircleOutlineIcon
                    titleAccess={
                      editingSub !== null
                        ? "Update this subtype entry"
                        : isDuplicatesub === true
                          ? "Question Subtype already exists .Please add another Question Subtype"
                          : !isSubFieldsFilled
                            ? "Please fill all fields before adding."
                            : ""
                    }
                    sx={{
                      fontSize: "30px",
                      color: "#009DFF",
                      cursor: "pointer",
                      opacity: editingSub !== null || isSubFieldsFilled ? 1 : 0.4,
                      pointerEvents:
                        editingSub !== null || isSubFieldsFilled ? "auto" : "none",
                    }}
                    onClick={() => {
                      const subtypes = methods.getValues("subtypes");
                      const subtype_counts = methods.getValues("subtype_counts");
                      const subtype_sequences =
                        methods.getValues("subtype_sequences");
                      if (subtypes && subtype_counts && subtype_sequences) {
                        handleAddDatasub();
                      } else {
                        if (!subtypes) methods.setError("subtypes", { message: "Required" });
                        if (!subtype_counts) methods.setError("subtype_counts", { message: "Required" });
                        if (!subtype_sequences) methods.setError("subtype_sequences", { message: "Required" });
                      }
                    }}
                  />
                )}
              </div>
              {validationMessages?.map((message: any, index: any) => (
                <div
                  key={index}
                  style={{
                    marginBottom: "10px",
                    color: "rgb(0 129 255)",
                  }}
                >
                  {message}
                </div>
              ))}
              {methods.watch("question_selection_type") !== 'predefined' && displayDataListsub.length > 0 && (
                <div
                  style={{
                    marginTop: "20px",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(45%, 1fr))", // 2 columns
                    gap: "10px",
                  }}
                >
                  {displayDataListsub.map((data, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "10px",
                        backgroundColor: "lightgrey",
                        borderRadius: "5px",
                        padding: "5px",
                        // width: "50%",
                        boxShadow: "0px 3px 9px darkgray",
                      }}
                    >
                      <div>
                        <div>
                          <b>QuestionType :</b>{" "}
                          {data?.questionTypes?.label ?? "Unknown"}
                        </div>
                        <div>
                          <b>Question Subtype :</b>{" "}
                          {data?.subtypes?.label ?? "Unknown"}
                        </div>
                        <div>
                          <b>Question Subcount :</b> {data?.subtype_counts ?? "N/A"}
                        </div>
                        <div>
                          <b>Sequence :</b> {data?.subtype_sequences ?? "N/A"}{" "}
                        </div>
                      </div>
                      {/* Buttons for each entry */}
                      <div style={{ display: "flex", gap: "5px" }}>
                        <IconButton onClick={() => handleEditSub(index)} size="small">
                          <EditIcon color="primary" fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteDatasub(index)}
                          size="small"
                        >
                          <DeleteIcon color="error" fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {methods.watch("question_selection_type") !== 'predefined' && (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Select
                name="status"
                label="Status"
                options={RoleSatus}
                value={
                  RoleSatus?.find(
                    (role) => role.value === methods.getValues("status")
                  ) ?? null
                }
                onChange={(_, value) => {
                  const val = value as AutocompleteOption;
                  methods.setValue("status", val?.value, { shouldValidate: true });
                }}
              />
              {errors.status && (
                <span
                  style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}
                >
                  {errors.status.message}
                </span>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={methods.watch("free_tier") === true}
                    onChange={() => {
                      methods.setValue("free_tier", !methods.watch("free_tier"), {
                        shouldValidate: true,
                      });
                    }}
                  />
                }
                label="Free Tier"
              />
            </div>
          )}

          {methods.watch("question_selection_type") !== 'predefined' && (
            <div style={{ color: "#009dff", fontSize: "15px", textAlign: "center", marginTop: "10px", width: "100%" }}>
              (Note: Click Free Tier to show exam to free users)
            </div>
          )}
        </form>
      </FormProvider >
      <ResponseDialog response={alertResponse} />
      <AlterDialog
        open={showSwitchWarning}
        setOpen={setShowSwitchWarning}
        alterheading="Unsaved Selections"
        alterableName="You have selected questions that haven't been added to the model yet. What would you like to do?"
        buttonname={
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', width: '100%' }}>
            <Button
              variant="contained"
              onClick={() => handleSwitchConfirm('add')}
              sx={{ bgcolor: '#009DFF', '&:hover': { bgcolor: '#007acc' } }}
            >
              Add & Switch
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleSwitchConfirm('discard')}
              sx={{ color: '#009DFF', borderColor: '#009DFF' }}
            >
              Discard & Switch
            </Button>
            <Button
              variant="text"
              onClick={() => {
                setShowSwitchWarning(false);
                setPendingSwitchData(null);
              }}
              sx={{ color: 'gray' }}
            >
              Cancel
            </Button>
          </Box>
        }
      />
    </>
  );
};
export default memo(ExamModelForm);
